import type {
  AspirationId,
  ThemeId,
  PlanGoal,
  PlanTenMinuteAction,
  PlanHabit,
  SevenDayStep,
  ImaginedVsChangeable,
} from "./types";
import type { ArchetypeId } from "@/lib/infiniteSelves/types";

/**
 * 3 Goal templates per aspiration (10 aspirations × 3 = 30 distinct templates).
 * Formatted as "something you could be doing within 4–8 weeks" with a measurable marker.
 */
export const GOAL_TEMPLATES: Record<
  AspirationId,
  [
    Omit<PlanGoal, "id" | "aspirationId">,
    Omit<PlanGoal, "id" | "aspirationId">,
    Omit<PlanGoal, "id" | "aspirationId">,
  ]
> = {
  health: [
    {
      title: "Establish a 20-minute evening wind-down and steady wake time",
      why: "Physical stamina and mood depend first on predictable sleep architecture.",
      measure: "Wake within the same 45-minute window for 18 out of the next 21 days.",
    },
    {
      title: "Build a consistent three-day weekly baseline movement routine",
      why: "Steady moderate movement restores daytime alertness without causing physical exhaustion.",
      measure: "Complete 12 brisk 25-minute movement sessions logged across four consecutive weeks.",
    },
    {
      title: "Prepare nourishing, home-cooked breakfasts four mornings each week",
      why: "Morning nutrition stabilizes blood sugar and reduces afternoon brain fog.",
      measure: "Enjoy 16 simple balanced morning meals prepared at home over one month.",
    },
  ],
  craft: [
    {
      title: "Complete a finished rough draft of a single creative piece",
      why: "Finishing one short work breaks the illusion that ideas must remain pristine in imagination.",
      measure: "A completed 1,500-word piece, recorded demo, or painted canvas stored in your portfolio.",
    },
    {
      title: "Dedicate four protected 45-minute studio sessions each week",
      why: "Craft develops through regular protected contact with materials, not sporadic inspiration.",
      measure: "Log 16 completed focus sessions in an analog notebook over the coming month.",
    },
    {
      title: "Curate and share a three-piece mini-collection with one trusted peer",
      why: "Gently placing real work in front of another person grounds creativity in the shared world.",
      measure: "Deliver three finished artifacts to a friend or mentor for constructive reflection.",
    },
  ],
  career: [
    {
      title: "Conduct four informational conversations in a field of genuine interest",
      why: "Direct dialogue with practitioners cuts through idealized assumptions about careers.",
      measure: "Complete four 20-minute conversational calls and send follow-up notes within six weeks.",
    },
    {
      title: "Overhaul and align your resume and public profile with your actual strengths",
      why: "Clear self-presentation allows opportunities to find you without exaggeration.",
      measure: "A polished one-page profile reviewed by two colleagues and saved as your active standard.",
    },
    {
      title: "Complete one recognized foundational module or micro-credential",
      why: "Tangible professional credentials convert abstract ambitions into visible capability.",
      measure: "A completed certificate, course project, or portfolio submission with documented feedback.",
    },
  ],
  relationships: [
    {
      title: "Establish a recurring weekly one-on-one catch-up with an existing friend",
      why: "Meaningful friendship is built through steady, predictable contact over long arcs.",
      measure: "Complete four weekly phone or coffee dates across the next month.",
    },
    {
      title: "Practice speaking one vulnerable, honest preference in a key relationship",
      why: "True intimacy requires letting people see what you actually need rather than guessing.",
      measure: "Three distinct moments where you state a real boundary or heartfelt preference clearly.",
    },
    {
      title: "Host or coordinate a simple low-pressure dinner or gathering for three people",
      why: "Creating hospitable space invites reciprocal community into your everyday life.",
      measure: "One relaxed shared gathering hosted or co-organized within five weeks.",
    },
  ],
  learning: [
    {
      title: "Read two comprehensive, reputable books on your chosen subject",
      why: "Long-form study creates interconnected understanding that short articles cannot supply.",
      measure: "Finish both volumes and write a one-page synthesis of their core models.",
    },
    {
      title: "Practice a new technical, language, or music skill for 20 minutes five days a week",
      why: "Neural adaptation thrives on frequent brief exposures rather than sporadic weekend marathons.",
      measure: "Log 20 completed micro-sessions in your tracking notebook over 30 days.",
    },
    {
      title: "Build and document one practical beginner project from scratch",
      why: "Active application exposes gaps in theory and cements practical competence.",
      measure: "A working code repository, translated article, or playable song recorded from start to end.",
    },
  ],
  adventure: [
    {
      title: "Plan and take two solo day excursions to completely unfamiliar territory",
      why: "Stepping outside your habitual geography awakens alertness and self-reliance.",
      measure: "Two full-day trips completed, with notes on what you noticed and learned.",
    },
    {
      title: "Try three novel physical or outdoor activities you have never attempted",
      why: "Physical novelty shakes up mental routines and builds somatic confidence.",
      measure: "Participate in three distinct beginner sessions (kayaking, climbing, bouldering, or trail hikes).",
    },
    {
      title: "Establish a weekend habit of walking an unmapped neighborhood for 90 minutes",
      why: "Adventure is primarily a stance of curiosity toward the world immediately around you.",
      measure: "Complete four exploratory walking routes without relying on phone navigation.",
    },
  ],
  money: [
    {
      title: "Track every expenditure for 30 consecutive days without self-judgment",
      why: "Financial clarity begins with honest observation before imposing strict austerity.",
      measure: "A full month of logged expenses categorized into essentials, discretionary, and savings.",
    },
    {
      title: "Build a $500 starter emergency buffer in a separate high-yield account",
      why: "Even a small cash buffer immediately lowers everyday stress and vulnerability.",
      measure: "Verify the dedicated savings account balance reaches or exceeds $500.",
    },
    {
      title: "Audit and cancel all unutilized subscriptions and renegotiate two utility bills",
      why: "Plugging quiet recurring leaks creates immediate monthly breathing room.",
      measure: "Document at least $35 in recurring monthly savings locked in for the year ahead.",
    },
  ],
  community: [
    {
      title: "Volunteer two hours every other week with a local hands-on organization",
      why: "Showing up consistently in your local area grounds your energy in real human mutual aid.",
      measure: "Complete four two-hour volunteer shifts logged over an eight-week span.",
    },
    {
      title: "Attend three consecutive public meetings, community workshops, or cleanups",
      why: "Civic belonging grows when you become a familiar, reliable presence in public spaces.",
      measure: "Attendance at three community gatherings with at least one person greeted by name.",
    },
    {
      title: "Organize a small mutual aid effort or tool-sharing initiative on your block",
      why: "Community begins with tangible neighborly support within walking distance of your door.",
      measure: "Connect with three immediate neighbors to share contact details and offer mutual support.",
    },
  ],
  calm: [
    {
      title: "Establish a strict 30-minute tech-free morning buffer before checking messages",
      why: "Protecting the first moments of consciousness preserves nervous system equilibrium.",
      measure: "20 morning buffers completed over the next 28 days without opening notifications.",
    },
    {
      title: "Create a quiet physical sanctuary corner in your home free from clutter and screens",
      why: "An uncluttered dedicated spot provides an immediate physical cue for decompression.",
      measure: "Sanctuary space maintained and visited for 15 minutes of quiet reading or tea daily.",
    },
    {
      title: "Take a weekly half-day digital sabbath with phone stored in a closed drawer",
      why: "Extended periods away from digital stimulation restore natural attention span.",
      measure: "Complete four four-hour screen-free weekend afternoons over the coming month.",
    },
  ],
  voice: [
    {
      title: "Write and publish or circulate three short personal essays or field notes",
      why: "Putting your perspective into clear language gives form to your internal observations.",
      measure: "Three pieces completed and shared with a study group, newsletter, or public forum.",
    },
    {
      title: "Speak up with an honest contribution in three team meetings or group circles",
      why: "Speaking before you feel completely ready trains your nervous system that your voice belongs.",
      measure: "Three distinct instances of sharing an unprompted opinion or thoughtful question.",
    },
    {
      title: "Practice setting a calm, non-defensive boundary with a colleague or family member",
      why: "A clear voice is most powerful when protecting your energy from unreasonable demands.",
      measure: "Two instances of stating a clear, respectful 'no' without over-explaining or apologizing.",
    },
  ],
};

/**
 * 3 Ten-minute actions per aspiration (doable in 10 minutes, concrete first step).
 */
export const TEN_MINUTE_ACTIONS: Record<AspirationId, [PlanTenMinuteAction, PlanTenMinuteAction, PlanTenMinuteAction]> = {
  health: [
    {
      title: "Hydrate, stretch, and set an alarm for tomorrow's wind-down",
      steps: [
        "Drink a tall glass of cold water right now.",
        "Do three gentle neck rolls and a 60-second forward fold to loosen tension.",
        "Set a repeating phone alarm for 9:45 PM labeled 'Lights dim, night begins'.",
      ],
    },
    {
      title: "Clear your bedside table of screens and clutter",
      steps: [
        "Move your phone charger at least six feet away from your mattress.",
        "Place a book, notebook, and pen on the nightstand instead.",
        "Leave a filled water bottle ready for morning.",
      ],
    },
    {
      title: "Put on walking shoes and complete one brisk lap around the block",
      steps: [
        "Slip on comfortable shoes without changing into full workout gear.",
        "Walk at a brisk pace for exactly five minutes down your street.",
        "Turn around, walk back, and notice how your breathing shifted.",
      ],
    },
  ],
  craft: [
    {
      title: "Write down three project ideas and star the one doable this week",
      steps: [
        "Open a fresh sheet of paper and write three rough creative concepts.",
        "Star the single idea that requires zero new purchases or software.",
        "Write the very first sentence, outline line, or sketch right below it.",
      ],
    },
    {
      title: "Clear your desk so only creative materials remain visible",
      steps: [
        "Clear away dishes, mail, and unrelated clutter from your working surface.",
        "Lay out your notebook, pens, instrument, or workspace shortcut.",
        "Leave it open to a blank page so starting tomorrow takes zero friction.",
      ],
    },
    {
      title: "Produce one intentionally rough two-minute micro-draft",
      steps: [
        "Set a timer on your phone for 120 seconds.",
        "Write, play, or draw continuously without pausing to erase or correct.",
        "Stop when the timer rings and close the notebook without rereading.",
      ],
    },
  ],
  career: [
    {
      title: "Identify one person whose work you admire and draft a brief note",
      steps: [
        "Find the contact email or profile of one professional doing work you respect.",
        "Draft a two-sentence note thanking them for a specific article, talk, or project.",
        "Save it in drafts to review with a calm eye before sending tomorrow.",
      ],
    },
    {
      title: "List your five core skills with one concrete proof point each",
      steps: [
        "Fold a sheet of paper into two columns: Skill and Real Evidence.",
        "Write down three to five skills you have actually exercised under pressure.",
        "Beside each, name one specific project, problem, or outcome that proves it.",
      ],
    },
    {
      title: "Review job postings in your target area and extract five repeated requirements",
      steps: [
        "Open two job descriptions for roles that genuinely appeal to you.",
        "Highlight the recurring tools, skills, or mindsets both posts mention.",
        "Circle the one requirement you already possess and star the one you will study next.",
      ],
    },
  ],
  relationships: [
    {
      title: "Send a warm, zero-obligation check-in text to an old friend",
      steps: [
        "Think of a friend who crossed your mind recently.",
        "Type: 'Thinking of you today! No need to reply quickly, just sending warm wishes.'",
        "Hit send and let the warmth sit without expecting an immediate conversation.",
      ],
    },
    {
      title: "Schedule one specific lunch or coffee date on your calendar",
      steps: [
        "Pick a free afternoon or morning slot next week.",
        "Message one friend with two specific dates and times: 'Are you free Tuesday at 2 or Friday at 10?'",
        "Lock the tentative placeholder on your calendar right now.",
      ],
    },
    {
      title: "Write a short note of genuine gratitude to someone who helped you",
      steps: [
        "Identify one person who offered guidance, kindness, or steady presence this month.",
        "Write three sentences naming exactly what their gesture meant to you.",
        "Send it via card, email, or message without asking for anything in return.",
      ],
    },
  ],
  learning: [
    {
      title: "Bookmark three high-quality foundational resources on your subject",
      steps: [
        "Search for the syllabus or reading list of a respected university course on the topic.",
        "Identify the primary introductory textbook or canonical essay.",
        "Bookmark it or place a library hold on the physical copy.",
      ],
    },
    {
      title: "Create a dedicated physical or digital study notebook",
      steps: [
        "Designate one clean notebook exclusively for this topic.",
        "Write the core question you want answered on the title page.",
        "Add a two-column index for concepts and page numbers.",
      ],
    },
    {
      title: "Read one introductory Wikipedia summary or overview article with pen in hand",
      steps: [
        "Open the overview article for your topic.",
        "Read for eight minutes, jotting down three unfamiliar terms you encounter.",
        "Pick one term to look up and define in your own words on your notepad.",
      ],
    },
  ],
  adventure: [
    {
      title: "Pin three unexplored local nature trails or historic landmarks",
      steps: [
        "Open a map of your surrounding region within a 45-minute radius.",
        "Locate three green spaces, reserves, or museums you have never visited.",
        "Save all three to a custom map list titled 'Weekend Expeditions'.",
      ],
    },
    {
      title: "Pack a lightweight daypack so taking off takes two minutes",
      steps: [
        "Find a small backpack or shoulder bag.",
        "Place a water bottle, sunscreen, small first aid kit, and notebook inside.",
        "Leave it hanging near your front door ready for spontaneous departures.",
      ],
    },
    {
      title: "Walk a route home that turns down streets you have never walked",
      steps: [
        "When walking today, deliberately turn down one unfamiliar side street.",
        "Keep your phone in your pocket and observe the architecture and garden details.",
        "Notice the physical sensation of stepping off your automatic pilot route.",
      ],
    },
  ],
  money: [
    {
      title: "Log into your main checking account and record your current balances",
      steps: [
        "Log in calmly without rushing or self-reproach.",
        "Write down your exact checking balance, savings balance, and any pending credit bill.",
        "Close the app. Simply looking at the real numbers dispels vague anxiety.",
      ],
    },
    {
      title: "Find and unsubscribe from one recurring subscription you rarely touch",
      steps: [
        "Open your app store subscriptions or bank transaction history.",
        "Spot one monthly charge you no longer actively celebrate.",
        "Click cancel right now. Congratulate yourself on keeping those dollars this month.",
      ],
    },
    {
      title: "Set up an automatic $10 weekly transfer to your savings account",
      steps: [
        "Log into your bank portal and locate recurring scheduled transfers.",
        "Set a repeating transfer of $10 every Monday morning.",
        "Name the target bucket 'Peace of Mind Fund'.",
      ],
    },
  ],
  community: [
    {
      title: "Look up your local community board, library calendar, or neighborhood group",
      steps: [
        "Search for your town or district library event calendar.",
        "Find one free public event happening within the next 14 days.",
        "Put the date and time on your personal calendar as an exploratory visit.",
      ],
    },
    {
      title: "Introduce yourself or offer a warm greeting to one immediate neighbor",
      steps: [
        "When stepping outside today, greet the first person you see on your block.",
        "Introduce yourself by first name if you haven't yet met.",
        "Keep it light, neighborly, and brief.",
      ],
    },
    {
      title: "Locate the nearest food pantry or donation box and check what items they need",
      steps: [
        "Search online for the nearest mutual aid pantry or donation drop-off.",
        "Read their current urgent needs list.",
        "Place two unopened cans or dry goods from your pantry into a bag by your door.",
      ],
    },
  ],
  calm: [
    {
      title: "Put phone on airplane mode and sit in silence for seven minutes",
      steps: [
        "Switch your mobile device into airplane mode and turn it face down.",
        "Sit comfortably in a chair with your feet flat on the floor.",
        "Rest your eyes and breathe naturally until your timer chimes, without trying to fix anything.",
      ],
    },
    {
      title: "Clear one flat surface in your living room completely",
      steps: [
        "Choose your coffee table, dining table, or entryway bench.",
        "Remove every single stray item, sorting trash and putting essentials away.",
        "Wipe the surface clean and leave it completely bare to give your eyes room to rest.",
      ],
    },
    {
      title: "Make a cup of herbal tea and drink it without any screen or audio",
      steps: [
        "Boil fresh water and brew chamomile, peppermint, or green tea.",
        "Leave your phone, computer, and headphones in another room.",
        "Sit and drink the tea while paying attention solely to the warmth and taste.",
      ],
    },
  ],
  voice: [
    {
      title: "Write down one opinion you genuinely hold but rarely say out loud",
      steps: [
        "Take a private index card or notebook page.",
        "Write: 'What I actually believe about [topic] is...'",
        "Fill the card with your honest, unhedged perspective without softening the edges.",
      ],
    },
    {
      title: "Draft a polite, firm boundary response for a common overreach",
      steps: [
        "Identify one request you frequently accept despite feeling resentful.",
        "Write out a clear refusal script: 'Thank you for asking, but I cannot take that on right now.'",
        "Read it aloud twice so your mouth gets familiar with saying the words calmly.",
      ],
    },
    {
      title: "Leave one thoughtful comment highlighting another creator's insight",
      steps: [
        "Find a thoughtful article, essay, or post you appreciated recently.",
        "Write two sentences articulating why their perspective mattered to your thinking.",
        "Post it openly under your real name or handles without self-effacement.",
      ],
    },
  ],
};

/**
 * 2 Habit templates per aspiration: [daily form, weekly form].
 */
export const HABIT_TEMPLATES: Record<AspirationId, [PlanHabit, PlanHabit]> = {
  health: [
    {
      cadence: "daily",
      text: "Drink a large glass of water and complete 10 gentle stretches.",
      cue: "Immediately after turning off your morning alarm.",
    },
    {
      cadence: "weekly",
      text: "Take a 60-minute weekend hike or brisk trail walk in natural sunlight.",
      cue: "Every Saturday morning right after your first cup of coffee.",
    },
  ],
  craft: [
    {
      cadence: "daily",
      text: "Open your notebook or workspace and work on your piece for 15 focused minutes.",
      cue: "Right after clearing the dinner table or concluding the workday.",
    },
    {
      cadence: "weekly",
      text: "Hold a dedicated two-hour uninterrupted creative studio block with phone in airplane mode.",
      cue: "Every Sunday afternoon between 2:00 PM and 4:00 PM.",
    },
  ],
  career: [
    {
      cadence: "daily",
      text: "Review industry developments or read one foundational essay for 12 minutes.",
      cue: "During your morning commute or right before opening your work email inbox.",
    },
    {
      cadence: "weekly",
      text: "Reach out to one peer or mentor to share notes, questions, or encouragement.",
      cue: "Every Friday at 11:00 AM before breaking for lunch.",
    },
  ],
  relationships: [
    {
      cadence: "daily",
      text: "Send one genuine message of appreciation, humor, or care to someone close to you.",
      cue: "When you pause for lunch at midday.",
    },
    {
      cadence: "weekly",
      text: "Share an in-person meal, coffee, or unhurried phone conversation with a friend.",
      cue: "Every Thursday evening or Saturday midday.",
    },
  ],
  learning: [
    {
      cadence: "daily",
      text: "Read 10 pages of your study book and jot down one summary bullet.",
      cue: "Right after getting into bed before turning off the bedside lamp.",
    },
    {
      cadence: "weekly",
      text: "Synthesize everything learned this week into a single one-page summary sheet.",
      cue: "Every Sunday morning over breakfast.",
    },
  ],
  adventure: [
    {
      cadence: "daily",
      text: "Take a 10-minute walk taking an unfamiliar street or noticing five unexpected visual details.",
      cue: "Immediately after concluding afternoon work responsibilities.",
    },
    {
      cadence: "weekly",
      text: "Visit a new park, market, museum, or neighborhood you have never visited before.",
      cue: "Every Saturday afternoon.",
    },
  ],
  money: [
    {
      cadence: "daily",
      text: "Log today's transactions into your notebook or tracking app in under 90 seconds.",
      cue: "Right before brushing your teeth at night.",
    },
    {
      cadence: "weekly",
      text: "Review weekly balances, check upcoming bills, and confirm savings transfers.",
      cue: "Every Sunday evening at 6:00 PM.",
    },
  ],
  community: [
    {
      cadence: "daily",
      text: "Offer a friendly greeting and brief conversation to someone you encounter locally.",
      cue: "When stepping into your neighborhood or picking up groceries.",
    },
    {
      cadence: "weekly",
      text: "Contribute one hour to a local volunteer role, community garden, or neighborhood cleanup.",
      cue: "Every other Saturday morning.",
    },
  ],
  calm: [
    {
      cadence: "daily",
      text: "Sit for eight minutes in silence or practice deep diaphragmatic breathing with eyes closed.",
      cue: "Immediately after pouring your morning tea or coffee.",
    },
    {
      cadence: "weekly",
      text: "Spend a full three-hour afternoon window completely disconnected from screens.",
      cue: "Every Sunday afternoon.",
    },
  ],
  voice: [
    {
      cadence: "daily",
      text: "Write down your honest reaction to one piece of news or personal event in your private journal.",
      cue: "Before shutting down your computer at the end of the day.",
    },
    {
      cadence: "weekly",
      text: "Share one considered thought, article recommendation, or boundary statement publicly or with peers.",
      cue: "Every Wednesday afternoon.",
    },
  ],
};

/**
 * 7-Day sequence per aspiration: 7 progressive, gentle, achievable steps.
 * Minutes tailored: scaled to time budget in the planner.
 */
export const SEVEN_DAY_SEQUENCES: Record<AspirationId, SevenDayStep[]> = {
  health: [
    { day: 1, title: "Audit & Hydrate", task: "Drink a large glass of water and note what time you went to sleep last night.", minutes: 5 },
    { day: 2, title: "Ten-Minute Walk", task: "Take a brisk walk around your block without wearing headphones.", minutes: 10 },
    { day: 3, title: "Evening Screen Cutoff", task: "Place your phone on its charger 20 minutes before sleeping and read a physical book.", minutes: 10 },
    { day: 4, title: "Nourishing Meal", task: "Prepare a meal that includes two colorful vegetables and plenty of protein.", minutes: 20 },
    { day: 5, title: "Gentle Mobility", task: "Do five simple stretches targeting hips, shoulders, and lower back.", minutes: 10 },
    { day: 6, title: "Nature Immersion", task: "Spend time outside in a park or quiet green space letting your eyes look into the distance.", minutes: 25 },
    { day: 7, title: "Weekly Rhythm Review", task: "Review your energy levels across the week and choose one sleep time to protect next week.", minutes: 10 },
  ],
  craft: [
    { day: 1, title: "Gather the Tools", task: "Find and lay out all materials or open files required for your creative project.", minutes: 8 },
    { day: 2, title: "Two-Minute Micro-Draft", task: "Produce an intentionally messy sketch, outline, or audio snippet without editing.", minutes: 8 },
    { day: 3, title: "Expanding the Core", task: "Add three specific details, sentences, or phrases that strengthen the concept.", minutes: 10 },
    { day: 4, title: "Focused Studio Sprint", task: "Work uninterrupted on the body of the piece with phone out of sight.", minutes: 25 },
    { day: 5, title: "Shape & Trim", task: "Reread or review your piece, cutting anything superfluous and clarifying the message.", minutes: 12 },
    { day: 6, title: "Complete the Draft", task: "Push the work to a stopping point where it can be considered a full first draft.", minutes: 25 },
    { day: 7, title: "Rest & Reflection", task: "Step back, do not edit, and note what felt most alive about making it.", minutes: 10 },
  ],
  career: [
    { day: 1, title: "Clarify Core Strengths", task: "Write down three problems you solved over the past year that you actually enjoyed.", minutes: 10 },
    { day: 2, title: "Scan the Landscape", task: "Find two job descriptions or organizations doing work that matches those strengths.", minutes: 10 },
    { day: 3, title: "One Outreach Draft", task: "Draft a brief, friendly informational inquiry to someone whose path you admire.", minutes: 12 },
    { day: 4, title: "Review & Send", task: "Reread your note with fresh eyes, ensure it asks one simple question, and send it.", minutes: 10 },
    { day: 5, title: "Portfolio Polish", task: "Update one paragraph of your online profile or resume to highlight your latest competence.", minutes: 15 },
    { day: 6, title: "Skill Gap Study", task: "Read one respected industry paper or tutorial on a tool you want to understand.", minutes: 25 },
    { day: 7, title: "Direction Check", task: "Write down your key takeaway from the week and set your primary career focus for next month.", minutes: 10 },
  ],
  relationships: [
    { day: 1, title: "Appreciation Ping", task: "Send a two-line message to a friend telling them something you appreciate about them.", minutes: 5 },
    { day: 2, title: "Phone-Free Meal", task: "Eat one meal with a partner, family member, or friend with devices kept in another room.", minutes: 20 },
    { day: 3, title: "Active Listening", task: "In a conversation today, ask one follow-up question and listen without planning your reply.", minutes: 10 },
    { day: 4, title: "Plan an Outing", task: "Coordinate a concrete date and time to see a friend next week.", minutes: 8 },
    { day: 5, title: "State a True Feeling", task: "Express an authentic emotion or gentle preference directly instead of saying 'I don't mind'.", minutes: 8 },
    { day: 6, title: "Shared Experience", task: "Spend an unhurried hour walking, cooking, or sharing an activity with someone you care about.", minutes: 30 },
    { day: 7, title: "Social Energy Check", task: "Reflect on which interactions nourished your energy and which felt draining.", minutes: 10 },
  ],
  learning: [
    { day: 1, title: "Define the Scope", task: "Write down the exact question or skill you want to master and why it matters to you.", minutes: 8 },
    { day: 2, title: "Find the Canonical Source", task: "Locate one reputable foundational textbook or course overview and acquire access.", minutes: 10 },
    { day: 3, title: "Read Chapter One", task: "Read the first 10 pages or introductory module with notebook open.", minutes: 15 },
    { day: 4, title: "Explain in Plain Words", task: "Write a three-sentence summary of the main concept as if explaining it to a 12-year-old.", minutes: 10 },
    { day: 5, title: "Practice Application", task: "Work through one practice problem, exercise, or code snippet yourself.", minutes: 20 },
    { day: 6, title: "Deep Focus Session", task: "Study for 25 continuous minutes without browser tabs or distractions open.", minutes: 25 },
    { day: 7, title: "Synthesize & Map", task: "Draw a simple visual map connecting all concepts learned this week and pick chapter two.", minutes: 10 },
  ],
  adventure: [
    { day: 1, title: "Micro-Route Change", task: "Walk or drive an unfamiliar route to a routine destination and notice what is different.", minutes: 10 },
    { day: 2, title: "Spot Three Wonders", task: "Take photos or notes of three architectural or natural details you normally overlook.", minutes: 8 },
    { day: 3, title: "Taste Something New", task: "Try a dish, tea, or ingredient from a culture different than your own.", minutes: 15 },
    { day: 4, title: "Map Discovery", task: "Research a nature trail, reserve, or neighborhood within one hour of your home.", minutes: 12 },
    { day: 5, title: "Pack the Bag", task: "Prepare your daypack with water, map, and essentials for a weekend exploration.", minutes: 10 },
    { day: 6, title: "The Expedition", task: "Visit the new trail or neighborhood and explore for at least 30 minutes on foot.", minutes: 35 },
    { day: 7, title: "Log the Field Notes", task: "Write a one-paragraph field note describing the trip and how your mind felt afterward.", minutes: 10 },
  ],
  money: [
    { day: 1, title: "Face the Balances", task: "Log in and write down your current balances calmly, without criticism.", minutes: 6 },
    { day: 2, title: "Review Past 7 Days", task: "Review the last seven days of transactions and highlight the three largest expenses.", minutes: 10 },
    { day: 3, title: "Cancel One Leak", task: "Find and cancel one subscription or membership you have not used in the past 60 days.", minutes: 8 },
    { day: 4, title: "Name Your Essentials", task: "Calculate the exact minimum amount you need each month for housing, food, and utilities.", minutes: 15 },
    { day: 5, title: "Automate a Micro-Save", task: "Set up an automatic recurring $5 or $10 weekly transfer into savings.", minutes: 10 },
    { day: 6, title: "Audit Recurring Bills", task: "Check your internet, insurance, or phone bill to see if lower tiers exist.", minutes: 20 },
    { day: 7, title: "Clarity Affirmation", task: "Acknowledge that facing money directly reduces anxiety, and set one goal for next month.", minutes: 8 },
  ],
  community: [
    { day: 1, title: "Look Around", task: "Look up your local community newspaper, bulletin board, or civic website.", minutes: 8 },
    { day: 2, title: "Say Hello", task: "Greet two neighbors or local store clerks by name or with genuine warmth.", minutes: 5 },
    { day: 3, title: "Find an Organization", task: "Identify one local community initiative, park cleanup, or food bank nearby.", minutes: 10 },
    { day: 4, title: "Small Contribution", task: "Drop off non-perishables at a donation box or pick up litter along your street.", minutes: 15 },
    { day: 5, title: "Attend an Event", task: "RSVP to or put on your calendar an upcoming public community meeting or event.", minutes: 8 },
    { day: 6, title: "Show Up in Person", task: "Attend a local farmers market, library talk, or public meeting.", minutes: 30 },
    { day: 7, title: "Reflect on Belonging", task: "Write down one way your everyday presence can support the people living near you.", minutes: 10 },
  ],
  calm: [
    { day: 1, title: "Ten Breaths", task: "Sit upright, inhale for four counts, exhale for six counts, repeated ten times.", minutes: 5 },
    { day: 2, title: "Clear the Desk", task: "Remove all papers and dishes from your immediate working desk so your gaze can rest.", minutes: 8 },
    { day: 3, title: "Quiet Transit", task: "Do your entire morning transit or walk in complete silence without audio or calls.", minutes: 15 },
    { day: 4, title: "Warm Drink Ritual", task: "Drink tea or warm water slowly without looking at any screen or reading material.", minutes: 10 },
    { day: 5, title: "Declutter a Surface", task: "Clear your bedside table or bathroom counter so it feels calm and spacious.", minutes: 10 },
    { day: 6, title: "The Screen-Free Afternoon", task: "Spend two hours outside or engaged in an analog hobby with phone turned off.", minutes: 30 },
    { day: 7, title: "Boundary for Next Week", task: "Identify one commitment you will say no to next week to protect your peace of mind.", minutes: 10 },
  ],
  voice: [
    { day: 1, title: "Private Opinion Card", task: "Write down one conviction you hold that you rarely share in polite conversation.", minutes: 8 },
    { day: 2, title: "Unhedged Sentence", task: "In conversation today, say 'I think...' instead of 'I could be wrong, but maybe...'", minutes: 5 },
    { day: 3, title: "Clear Preference", task: "When asked what you want for lunch or what movie to watch, state a clear choice.", minutes: 5 },
    { day: 4, title: "Write the Draft", task: "Write a 250-word reflection on a topic that matters to your core values.", minutes: 15 },
    { day: 5, title: "Practice a Boundary", task: "Practice saying: 'I understand, but that won't work for me right now.'", minutes: 8 },
    { day: 6, title: "Share Your Perspective", task: "Share your perspective with a group, friend, or forum on a topic you care about.", minutes: 20 },
    { day: 7, title: "Notice the Shift", task: "Reflect on how your body felt when speaking honestly rather than holding back.", minutes: 10 },
  ],
};

/**
 * Meaning sentence fragments for each Theme (what the imagined life may be pointing at).
 */
export const THEME_MEANINGS: Record<ThemeId, string> = {
  freedom:
    "a longing for genuine autonomy, a schedule you control, and fewer arbitrary constraints on your hours",
  love:
    "a hunger for deep mutual intimacy, being fully seen without pretense, and reliable emotional safety",
  confidence:
    "a desire to trust your own instincts, hold your ground calmly, and navigate decisions without chronic second-guessing",
  creativity:
    "an urge to bring original ideas into physical reality, explore aesthetic forms, and feel generative",
  adventure:
    "an appetite for novelty, sensory alertness, and stepping beyond comfortable predictable routines",
  achievement:
    "a healthy ambition to build demonstrable competence, finish substantial work, and earn genuine respect",
  belonging:
    "a deep need for recognized place within a community, shared traditions, and contributing to others",
  purpose:
    "a hunger for alignment where your everyday work and energy serve a clear, meaningful mission",
};

/**
 * Fictional archetype reflections (what each fictional persona shows, strictly labelled as fiction).
 */
export const ARCHETYPE_INSIGHTS: Record<ArchetypeId, string> = {
  divergent:
    "In fictional multiverse terms, this alternate self explores the roads not taken — highlighting everyday relationships, craftsmanship, and grounded human presence that you may yearn to cultivate right here.",
  ascendant:
    "In fictional multiverse terms, this cosmic-scale self operates at the heights of technological mastery — symbolizing your appetite for deep systems, intellectual scope, and building things that outlast you.",
  strange:
    "In fictional multiverse terms, this alien-physics explorer steps into profound unorthodoxy — reflecting your willingness to think unconventionally, question baseline assumptions, and embrace original paths.",
  unexpected:
    "In fictional multiverse terms, this inverted counterpart challenges familiar scripts — reminding you that aspects of your identity you rarely express still hold dormant energy and potential.",
};

/**
 * 5 Weekly reflection prompts to support continuous mindful check-ins.
 */
export const WEEKLY_REFLECTION_PROMPTS: readonly string[] = [
  "Which action this week felt surprisingly easy, and which one required the most emotional effort?",
  "Did you notice moments where daydreaming stepped in to replace taking action, or did your imagination inspire what you did?",
  "What is one small boundary or habit you want to adjust for the coming seven days?",
  "How did your energy and physical well-being respond to the changes you practiced?",
  "What did you learn about the difference between your imagined version of this value and its real-world practice?",
] as const;

/**
 * Imagined vs. Changeable contrast per aspiration.
 */
export const IMAGINED_VS_CHANGEABLE: Record<AspirationId, ImaginedVsChangeable> = {
  health: {
    imagined: [
      "Effortless endless energy without physical fatigue or aging.",
      "An instantly transformed physique without weeks of consistent nourishment and rest.",
      "Complete immunity to biological stress or sleep deprivation.",
    ],
    changeable: [
      "Setting a consistent bedtime and waking rhythm that supports daytime alertness.",
      "Replacing 20 minutes of sedentary screen time with daily moderate movement.",
      "Drinking enough water and cooking simple nourishing meals at home.",
    ],
    note: "Fantasy provides immediate invulnerability; real life offers steady physical vitality through simple daily rhythms.",
  },
  craft: {
    imagined: [
      "Producing finished masterpieces instantly without messy drafts or self-doubt.",
      "Universal acclaim and understanding from an adoring audience.",
      "Creating exclusively during spontaneous bursts of mystical inspiration.",
    ],
    changeable: [
      "Protecting 30 minutes of undisturbed studio or writing time several days a week.",
      "Embracing rough, awkward first drafts as the necessary raw material of craft.",
      "Finishing a short, contained project and sharing it with one supportive peer.",
    ],
    note: "Fantasy visualizes only the applause; real craft is built in the quiet, enjoyable work of shaping raw materials.",
  },
  career: {
    imagined: [
      "Holding absolute influence and authority without administrative headaches or politics.",
      "An effortless vocational leap where everyone immediately recognizes your brilliance.",
      "A work life completely free from repetitive tasks or difficult negotiations.",
    ],
    changeable: [
      "Clarifying your authentic strengths and updating your professional portfolio to reflect them.",
      "Reaching out to practitioners for honest informational conversations about their daily reality.",
      "Building one concrete credential or project that demonstrates your capability.",
    ],
    note: "Fantasy skips the apprentice stage; real career growth unfolds through curiosity, competence, and steady networking.",
  },
  relationships: {
    imagined: [
      "Telepathic understanding where partners and friends anticipate every need without words.",
      "A relationship completely free from misunderstanding, friction, or boundary friction.",
      "Being loved unconditionally while never revealing your awkward or vulnerable thoughts.",
    ],
    changeable: [
      "Reaching out consistently to friends with low-pressure check-ins and invitations.",
      "Practicing honest, calm communication about what you actually need and feel.",
      "Listening attentively to others without trying to manage their impressions.",
    ],
    note: "Fantasy imagines effortless harmony; real intimacy is built through brave vulnerability and showing up over time.",
  },
  learning: {
    imagined: [
      "Instantly absorbing entire disciplines and languages without practice or memorization.",
      "Effortless intellectual authority without the humility of being a clumsy beginner.",
      "Retaining every concept after reading it once without revision.",
    ],
    changeable: [
      "Reading 10 focused pages of foundational literature every evening.",
      "Working through beginner exercises and accepting errors as learning data.",
      "Explaining concepts simply to yourself or peers to solidify true understanding.",
    ],
    note: "Fantasy wants the badge of expertise; real learning finds genuine joy in the beginner's messy curiosity.",
  },
  adventure: {
    imagined: [
      "Teleporting to exotic horizons without logistics, fatigue, or budget constraints.",
      "Living in perpetual cinematic drama where routine never exists.",
      "Finding an idyllic foreign place that permanently resolves internal restlessness.",
    ],
    changeable: [
      "Exploring unmapped parks, neighborhoods, and trails within an hour of your home.",
      "Trying novel activities, sports, or cuisines that pull you out of automatic habits.",
      "Cultivating an explorer's curious attention toward your ordinary surroundings.",
    ],
    note: "Fantasy imagines escaping yourself; real adventure is discovering how alive you can feel right where you are.",
  },
  money: {
    imagined: [
      "Boundless wealth that eliminates all vulnerability and guarantees eternal security.",
      "Never having to think about costs, trade-offs, or balancing priorities.",
      "Sudden windfall solutions that bypass steady budgeting and patience.",
    ],
    changeable: [
      "Tracking daily spending honestly and understanding where your money actually flows.",
      "Building a steady emergency cash buffer that absorbs life's surprise expenses.",
      "Aligning expenditures with what you truly value while letting go of status purchases.",
    ],
    note: "Fantasy equates safety with limitless luxury; real financial peace begins with clarity, buffer, and conscious choices.",
  },
  community: {
    imagined: [
      "A tight-knit utopian clan where everyone shares your exact ideals and sensibilities.",
      "Being celebrated as a civic pillar without attending mundane organizing meetings.",
      "Total communal harmony without conflicting personalities or messy compromises.",
    ],
    changeable: [
      "Showing up regularly to neighborhood gatherings, library events, or volunteer shifts.",
      "Greeting local shopkeepers and neighbors with reliable warmth and respect.",
      "Contributing practical help to tangible shared projects in your zip code.",
    ],
    note: "Fantasy dreams of a bespoke tribe; real community is about learning to love the actual, imperfect people next door.",
  },
  calm: {
    imagined: [
      "A serene mountaintop retreat where external demands, bills, and notifications never intrude.",
      "A mind that permanently halts thoughts and achieves unbroken tranquility.",
      "Absolute isolation where other people's needs cannot disturb your equilibrium.",
    ],
    changeable: [
      "Establishing a 30-minute quiet morning buffer before opening phones and email.",
      "Practicing eight minutes of mindful breathing or silent stillness daily.",
      "Decluttering your living surfaces and taking weekly screen-free afternoon breaks.",
    ],
    note: "Fantasy seeks total isolation; real calm is an internal harbor built through steady boundaries in the middle of life.",
  },
  voice: {
    imagined: [
      "Delivering breathtaking speeches that leave everyone mesmerized and converted instantly.",
      "Speaking your mind without ever encountering disagreement, pushback, or tension.",
      "Possessing flawless, unshakeable charisma in every social interaction.",
    ],
    changeable: [
      "Writing down your genuine thoughts in private before entering difficult meetings.",
      "Practicing concise, polite boundary statements when requests exceed your capacity.",
      "Sharing your perspective in small, honest ways even when your voice shakes a little.",
    ],
    note: "Fantasy imagines effortless dominance; real voice is the quiet courage to speak truthfully and let others respond as they will.",
  },
};

/**
 * Honest limits statements affirming what the plan can and cannot promise.
 */
export const HONEST_LIMITS: readonly string[] = [
  "This plan provides practical structure and grounded suggestions. It cannot guarantee specific external outcomes, vocational offers, or relational responses, because those involve factors outside anyone's direct control.",
  "Real-world change takes patience, iterative experiments, and self-compassion. This framework is a thoughtful map for small daily moves, not a magical overnight transformation.",
  "Imagining alternate lives is a natural and creative capacity. Turning those values into reality does not mean matching every fantasy detail; it means bringing their real underlying essence into your everyday hours.",
  "Life includes unexpected interruptions, low-energy days, and competing obligations. Progress is measured by how gently and steadily you return to your practice, not by continuous consistency.",
] as const;
